import { useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp, _inp } from '../../components/ui/Inputs.jsx';
import { dateSuisse } from '../../lib/dates';
import { MaSecurite } from './MaSecurite.jsx';
import { tr, trf } from '../../lib/i18n';
import { telechargeLaSauvegarde } from '../../lib/sauvegarde';
import { C } from '../../theme/palette';

/** Le compte SwissTech de l'entreprise : sa suppression, au patron seul. */
export const compteApi = {
  etat: () => api.get('/compte/suppression'),
  canaux: () => api.get('/compte/suppression/canaux'),
  code: (email, motDePasse, canal) =>
    api.post('/compte/suppression/code', { email, motDePasse, canal }),
  demande: (nom, email, motDePasse, defi, code) =>
    api.post('/compte/suppression', { nom, email, motDePasse, defi, code }),
  annule: () => api.delete('/compte/suppression'),
  recupere: (id) => api.post('/compte/suppression/recuperation/' + id),
};

/** Libellé de chaque canal du code de confirmation. */
const CANAUX = {
  EMAIL: '✉️ Par e-mail',
  SMS: '📱 Par SMS',
  APPLICATION: '🔑 Application Authenticator',
};

/**
 * Les autres sociétés du patron dont la suppression est demandée, chacune
 * avec son bouton « Récupérer » : celle qu'on a quittée pour en ouvrir une
 * nouvelle ne se perd pas pendant les trois mois.
 *
 * @param {object} props
 * @param {Array} props.autres sociétés en suppression
 * @param {Function} props.onRecupere récupère une société par son id
 * @param {boolean} [props.enCours] une demande est en route
 * @returns {JSX.Element|null} la liste, ou rien
 */
function AutresEnSuppression({ autres, onRecupere, enCours }) {
  if (!autres?.length) return null;
  return autres.map((a) => (
    <div
      key={a.id}
      role="alert"
      style={{
        background: '#FEF2F2',
        border: '1px solid ' + C.red,
        borderRadius: 10,
        padding: '10px 12px',
        fontSize: 11.5,
        lineHeight: 1.5,
        color: C.ink,
      }}
    >
      <b>{trf('{0} sera supprimée le {1}.', a.nom || '', dateSuisse(a.effaceLe))}</b>
      <div>Jusque-là, vous pouvez la récupérer avec toutes ses données.</div>
      <div style={{ marginTop: 8 }}>
        <Btn
          label={trf('↩︎ Récupérer {0}', a.nom || '')}
          color={C.green}
          full
          onClick={enCours ? undefined : () => onRecupere(a.id)}
        />
      </div>
    </div>
  ));
}

/**
 * Son nom, tel qu'il paraît sur les bons d'achat, courriers et e-mails.
 *
 * <p>« J'ai modifié mon nom, mais ça sort toujours avec petit arsim » : le nom
 * venait de l'inscription, sans endroit pour le corriger. Il change ici pour
 * le compte, la fiche d'employé rattachée et les bons d'achat déjà établis.
 *
 * @returns {JSX.Element} les deux champs et le bouton
 */
function MonNom() {
  const { session, adopteCompte } = useSession();
  const actuel = (session?.nom || '').trim();
  const [prenom, setPrenom] = useState(() => actuel.split(/\s+/)[0] || '');
  const [nom, setNom] = useState(() => actuel.split(/\s+/).slice(1).join(' '));
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const change = (prenom + ' ' + nom).trim() !== actuel;
  const enregistre = async () => {
    setEnCours(true);
    setErreur('');
    try {
      adopteCompte(await authApi.renomme(prenom.trim(), nom.trim()));
    } catch (e) {
      setErreur(e?.message || tr('L’enregistrement a échoué.'));
    } finally {
      setEnCours(false);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.navy }}>
        {tr('Ton nom')} : {actuel}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Inp label="Prénom" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
        <Inp label="Nom" value={nom} onChange={(e) => setNom(e.target.value)} />
      </div>
      {erreur && <div style={{ fontSize: 11, color: C.red }}>{erreur}</div>}
      <Btn
        label="💾 Enregistrer"
        ghost
        color={C.navy}
        full
        disabled={enCours || !change || !prenom.trim() || !nom.trim()}
        onClick={enregistre}
      />
    </div>
  );
}

/**
 * « Mon compte » dans Réglages : se déconnecter, télécharger ses données,
 * et — pour le patron seulement, et nulle part ailleurs — supprimer le compte
 * SwissTech de l'entreprise : nom de l'entreprise, adresse, mot de passe et
 * code reçu par e-mail ou par SMS, ou lu dans l'application Authenticator ;
 * trois mois pour changer d'avis, et récupérer une autre de ses sociétés.
 *
 * @param {object} props
 * @param {object} [props.soc] l'entreprise
 * @returns {JSX.Element} la carte
 */
export function MonCompteCard({ soc }) {
  const { session, deconnexion } = useSession();
  const patron = session?.role === 'patron';
  const [etat, setEtat] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [canaux, setCanaux] = useState(['EMAIL']);
  const [defi, setDefi] = useState('');
  const [canal, setCanal] = useState('');
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState('');
  const [info, setInfo] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!patron) return undefined;
    let vivant = true;
    Promise.resolve()
      .then(() => compteApi.etat())
      .then((e) => {
        if (vivant) setEtat(e || null);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [patron]);

  const sauvegarde = async () => {
    setErreur('');
    try {
      const fichier = await telechargeLaSauvegarde();
      setInfo('✓ Données téléchargées : ' + fichier);
    } catch (e) {
      setErreur(e?.message || 'Le téléchargement a échoué.');
    }
  };

  const ouvre = () => {
    setOuvert(true);
    setErreur('');
    Promise.resolve()
      .then(() => compteApi.canaux())
      .then((c) => {
        if (c?.canaux?.length) setCanaux(c.canaux);
      })
      .catch(() => {});
  };

  const ferme = () => {
    setOuvert(false);
    setNom('');
    setEmail('');
    setMotDePasse('');
    setDefi('');
    setCanal('');
    setCode('');
  };

  const recoitLeCode = async (choisi) => {
    setEnCours(true);
    setErreur('');
    setInfo('');
    try {
      const r = await compteApi.code(email, motDePasse, choisi);
      setDefi(r?.defi || '');
      setCanal(choisi);
      setCode('');
    } catch (e) {
      setErreur(e?.message || 'Le code n’a pas pu être envoyé.');
    } finally {
      setEnCours(false);
    }
  };

  const demande = async () => {
    setEnCours(true);
    setErreur('');
    try {
      setEtat(await compteApi.demande(nom, email, motDePasse, defi, code.trim()));
      ferme();
    } catch (e) {
      setErreur(e?.message || 'La demande n’a pas abouti.');
    } finally {
      setEnCours(false);
    }
  };

  const recupere = async (id) => {
    setEnCours(true);
    setErreur('');
    try {
      setEtat(await compteApi.recupere(id));
      setInfo('✓ Société récupérée : sa suppression est annulée.');
    } catch (e) {
      setErreur(e?.message || 'La récupération a échoué.');
    } finally {
      setEnCours(false);
    }
  };

  const annule = async () => {
    setEnCours(true);
    setErreur('');
    try {
      setEtat(await compteApi.annule());
      setInfo('✓ Suppression annulée : votre compte reste.');
    } catch (e) {
      setErreur(e?.message || 'L’annulation a échoué.');
    } finally {
      setEnCours(false);
    }
  };

  const enSuppression = !!etat?.demandeeLe;

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar icon="👤" title="Mon compte" color={C.navy} sub={session?.email || ''} />
      <div style={{ padding: 13, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <MonNom />
        <MaSecurite />
        <Btn label="🚪 Se déconnecter" color={C.navy} full onClick={() => deconnexion()} />
        {patron && (
          <Btn
            label="💾 Télécharger toutes mes données"
            ghost
            color={C.navy}
            full
            onClick={sauvegarde}
          />
        )}
        {patron && (
          <AutresEnSuppression autres={etat?.autres} onRecupere={recupere} enCours={enCours} />
        )}
        {patron && enSuppression && (
          <div
            role="alert"
            style={{
              background: '#FEF2F2',
              border: '1px solid ' + C.red,
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 11.5,
              lineHeight: 1.5,
              color: C.ink,
            }}
          >
            <b>{trf('Suppression demandée le {0}.', dateSuisse(etat.demandeeLe))}</b>
            <div>
              {trf(
                'Le compte et toutes ses données seront effacés le {0}. Jusque-là, vous pouvez l’annuler.',
                dateSuisse(etat.effaceLe),
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              <Btn
                label="↩︎ Annuler la suppression"
                color={C.green}
                full
                onClick={enCours ? undefined : annule}
              />
            </div>
          </div>
        )}
        {patron && !enSuppression && !ouvert && (
          <Btn label="🗑 Supprimer mon compte SwissTech" ghost color={C.red} full onClick={ouvre} />
        )}
        {patron && !enSuppression && ouvert && (
          <div
            style={{
              border: '1px solid ' + C.red,
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 11.5,
              lineHeight: 1.5,
              color: C.ink,
            }}
          >
            <div style={{ fontWeight: 800, color: C.red, marginBottom: 4 }}>
              {trf('Supprimer le compte de {0}', soc?.nom || '')}
            </div>
            <div style={{ marginBottom: 8 }}>
              Tout sera effacé dans 3 mois : clients, offres, factures, chantiers, photos, employés
              et leurs accès. Jusque-là, vous pouvez récupérer le compte. Téléchargez d’abord vos
              données : la loi vous demande de garder vos pièces comptables 10 ans.
            </div>
            {etat?.groupe?.length > 1 && (
              <div
                data-testid="suppression-groupe"
                style={{
                  marginBottom: 8,
                  padding: '7px 9px',
                  borderRadius: 8,
                  background: C.redL,
                  fontWeight: 700,
                }}
              >
                {trf(
                  'Compte mix : les deux sociétés seront supprimées ensemble ({0}), et récupérées ensemble.',
                  etat.groupe.join(' + '),
                )}
              </div>
            )}
            <Inp
              label="Retapez le nom de l’entreprise"
              value={nom}
              placeholder={soc?.nom || ''}
              onChange={(e) => setNom(e.target.value)}
            />
            <Inp
              label="Votre adresse e-mail"
              type="email"
              value={email}
              placeholder={session?.email || ''}
              onChange={(e) => {
                setEmail(e.target.value);
                setDefi('');
              }}
            />
            <Inp
              label="Votre mot de passe"
              type="password"
              value={motDePasse}
              onChange={(e) => {
                setMotDePasse(e.target.value);
                setDefi('');
              }}
            />
            {!defi && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  Recevoir le code de confirmation :
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {canaux.map((c) => (
                    <Btn
                      key={c}
                      label={CANAUX[c] || c}
                      ghost
                      color={C.navy}
                      full
                      onClick={
                        enCours || !nom.trim() || !email.trim() || !motDePasse
                          ? undefined
                          : () => recoitLeCode(c)
                      }
                    />
                  ))}
                </div>
              </div>
            )}
            {defi && (
              <>
                <div style={{ marginBottom: 6 }}>
                  {canal === 'APPLICATION'
                    ? 'Ouvrez votre application Authenticator et tapez le code SwissTech.'
                    : canal === 'SMS'
                      ? 'Un code à 6 chiffres vient de partir par SMS.'
                      : 'Un code à 6 chiffres vient de partir par e-mail.'}
                </div>
                <Inp label="Code de confirmation">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    aria-label="Code de confirmation"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    style={_inp}
                  />
                </Inp>
                <button
                  type="button"
                  onClick={() => setDefi('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: C.teal,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '0 0 8px',
                    cursor: 'pointer',
                  }}
                >
                  Recevoir le code autrement
                </button>
              </>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Btn label="Annuler" ghost color={C.grey} full onClick={ferme} />
              </div>
              <div style={{ flex: 1 }}>
                <Btn
                  label={enCours ? 'Envoi…' : '🗑 Supprimer'}
                  color={C.red}
                  full
                  onClick={enCours || !defi || code.trim().length !== 6 ? undefined : demande}
                />
              </div>
            </div>
          </div>
        )}
        {erreur && (
          <div role="alert" style={{ fontSize: 11, color: C.red, fontWeight: 700 }}>
            ✕ {erreur}
          </div>
        )}
        {info && (
          <div role="status" style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>
            {info}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Rappel en tête de l'accueil quand la suppression du compte est demandée :
 * le patron sait qu'elle approche, et où l'annuler.
 *
 * @param {object} props
 * @param {Function} props.onNav navigation
 * @returns {JSX.Element|null} le rappel, ou rien
 */
export function BandeauSuppression({ onNav }) {
  const { session } = useSession();
  const [etat, setEtat] = useState(null);
  useEffect(() => {
    if (session?.role !== 'patron') return undefined;
    let vivant = true;
    Promise.resolve()
      .then(() => compteApi.etat())
      .then((e) => {
        if (vivant) setEtat(e || null);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [session?.role]);
  if (!etat?.demandeeLe && !etat?.autres?.length) return null;
  if (!etat?.demandeeLe) {
    return (
      <div
        role="alert"
        onClick={() => onNav && onNav('reglages')}
        style={{
          background: '#FEF2F2',
          border: '1px solid ' + C.red,
          borderRadius: 11,
          padding: '10px 12px',
          marginBottom: 12,
          fontSize: 11.5,
          color: C.ink,
          cursor: 'pointer',
          lineHeight: 1.5,
        }}
      >
        {etat.autres.map((a) => (
          <div key={a.id}>
            ⚠️ <b>{trf('{0} sera supprimée le {1}.', a.nom || '', dateSuisse(a.effaceLe))}</b>
          </div>
        ))}
        <div>Pour la récupérer : Réglages → Mon compte.</div>
      </div>
    );
  }
  return (
    <div
      role="alert"
      onClick={() => onNav && onNav('reglages')}
      style={{
        background: '#FEF2F2',
        border: '1px solid ' + C.red,
        borderRadius: 11,
        padding: '10px 12px',
        marginBottom: 12,
        fontSize: 11.5,
        color: C.ink,
        cursor: 'pointer',
        lineHeight: 1.5,
      }}
    >
      ⚠️ <b>{trf('Votre compte SwissTech sera supprimé le {0}.', dateSuisse(etat.effaceLe))}</b>
      <div>Pour l’annuler : Réglages → Mon compte.</div>
    </div>
  );
}
